export interface SupportFaqLink {
  to: string;
  label: string;
}

export interface SupportFaqItem {
  id: string;
  question: string;
  /** Plain answer paragraphs shown before any step list */
  paragraphs?: readonly string[];
  /** Ordered how-to steps */
  steps?: readonly string[];
  /** Optional in-app link (e.g. Get the App) */
  link?: SupportFaqLink;
}

export const supportFaqEn = {
  title: 'Support',
  lead:
    'Answers for using Portcierge on iPhone and on the web. If you still need help, contact us below.',
  faqHeading: 'Frequently asked questions',
  ctaTitle: 'Still need help?',
  ctaText: 'Reach out and we will get back to you as soon as we can.',
  ctaButton: 'Contact us',
  items: [
    {
      id: 'what-is-portcierge',
      question: 'What is Portcierge?',
      paragraphs: [
        'Portcierge is a planning tool and organizer for your cruise—embarkation, sea days, and port days in one place, with weather, options lists, and a shareable itinerary.',
      ],
    },
    {
      id: 'iphone-and-web',
      question: 'Can I use Portcierge on iPhone and on the web?',
      paragraphs: [
        'Portcierge is designed to work completely from your iPhone. Either iPhone or web will work for planning.',
        'If you use the web version, you will not have offline access to data. On iPhone, your trip data stays available local-first while you are underway.',
      ],
    },
    {
      id: 'get-iphone-app',
      question: 'How do I get the iPhone app?',
      paragraphs: [
        'Download Portcierge from the App Store.',
      ],
      link: {
        to: '/app',
        label: 'Get the App on the App Store',
      },
    },
    {
      id: 'confirmations',
      question: 'How do I get confirmations into the itinerary?',
      paragraphs: [
        'Forward booking confirmations to your Portcierge inbound email address, then import them into the right cruise and port day.',
      ],
      steps: [
        'Sign in on iPhone or web.',
        'Open Settings.',
        'Under your account, find Inbound email—that is your Portcierge address for booking confirmations. Use Copy to copy it.',
        'Forward confirmation emails (or have them sent) to that inbound address.',
        'Only addresses on your Allowed Sender Addresses list (Settings → Allowed Sender Addresses) can send into Portcierge. Add any alternate from-addresses you use.',
        'Open the confirmations inbox (from Settings / Allowed Senders “Open confirmations inbox,” or the trips inbound inbox on web) to review items, then Import to trip into the right cruise or port day.',
        'On iPhone you can also use the Share Sheet to send URLs, text, PDFs, and files into the Shared Items inbox, then save them into the right port.',
      ],
    },
    {
      id: 'add-cruise',
      question: 'How do I add or build a cruise?',
      paragraphs: [
        'Create or import a sailing with embarkation, sea days, and port calls on iPhone or web so day-by-day planning stays consistent as ports change.',
      ],
    },
    {
      id: 'ai-planning',
      question: 'How does AI planning work?',
      paragraphs: [
        'AI planning is optional. Portcierge can draft a port-day plan and help extract details from shared items.',
        'It is review-first—you choose what is added to your cruise. Some AI features require a subscription.',
      ],
    },
    {
      id: 'share-itinerary',
      question: 'Can I share my itinerary with travel companions?',
      paragraphs: [
        'Export a cruise itinerary PDF so companions have the plan even if they are not using Portcierge.',
      ],
    },
    {
      id: 'sign-in-sync',
      question: 'I can’t sign in or see my cruise on both iPhone and web',
      paragraphs: [
        'Use the same account on both, check your network, and try again. Remember the web version has no offline copy of your data.',
        'If it still fails, use Contact us below.',
      ],
    },
  ] satisfies readonly SupportFaqItem[],
} as const;
